<template>
  <main id="watchlistPage">

    <section id="emptyPortfolio">
      <EmptyPortfolio v-if="!isLoading && watchlist.length === 0"/>
    </section>


    <TableToolbar :buttons="['info']" :info="dictionary.messages.watchlist">

      <template #main>


        <div class="searchBar" id="searchBar">

          <div
              :dir="uiDirection"
              @click="removeSelected()"
              v-if="selectedSymbols.length > 0"
              class="removeMultipleButton"
          >
            {{ dictionary.watchlist.removeSymbols }} ({{ selectedSymbols.length }})
          </div>

          <div id="firstWrapper">
            <div id="searchWrapper" :dir="uiDirection">
              <i class="ri-search-2-line"/>
              <input type="search"
                     ref="searchInput"
                     :placeholder="dictionary.symbolSearch.inputLabel" v-model="searchQuery"/>

            </div>
            <toggle-switch :onoff="true" :state="allowNotifications"
                           :text="dictionary.settingsPage.notificationLabels.emailNotifications"
                           @stateChanged="allowNotificationsChange"/>
          </div>


          <div class="watchlistSize" :class="{listFull: watchlist.length === 200}"
               v-if="!isLoading && watchlist.length > 0">
            {{ dictionary.watchlist.numberOfSymbols }}: {{ watchlist.length }}/{{ 200 }}
          </div>
        </div>
      </template>


    </TableToolbar>


    <DataGridTable :data="watchlistSymbols"
                   v-if="!isLoading && watchlist.length > 0"
                   :loaded="isLoading"
                   :height-elements="['searchBar', 'expl']"
                   :default-sort="'symbol'"
                   :extra-headers="[{position: 1}]"
                   :height-decrease="25"
                   :default-sort-direction="1"
                   :fixed-col="0">

      <template #thead="{thClick, sortKey, sortDirection, toggleSymbolActions, showSymbolActions, headers}">
        <HeadCell v-for="(th, thIndex) in headers"
                  :sort-key="sortKey"
                  :header="th"
                  :center="thIndex > 4"
                  :sort-direction="sortDirection"
                  :sort-value="th.sortValue"
                  v-show="th.showIf === undefined || th.showIf()"
                  :showSymbolActions="showSymbolActions"
                  @toggleSymbolActions="toggleSymbolActions()"
                  @sort="thClick(th.sortValue, thIndex)">
          {{ th.label }}
        </HeadCell>
      </template>
      <template #tbody="{rows, showSymbolActions}">
        <tr v-for="(row, rowIndex) in rows" :key="`wl-${row.symbol}`">
          <td>
            <div class="symbolCell">

              <label>
                <input
                    type="checkbox"
                    :checked="selectedSymbols.includes(row.symbol)"
                    @change="toggleMultiSelect(row.symbol)"
                />
              </label>

              <symbol-tag :tags="row.tags" :showSymbolActions="showSymbolActions"
                          :options="{link: true, chart: true, logo: 'small', watchlist: false}"
                          :symbol="row.symbol"/>
            </div>
          </td>
          <td class="" style="padding: 0 !important;">
            <div class="settings">
              <div :dir="uiDirection" class="addRemoveSymbol remove"
                   @click="removeSymbol(row, symbolIdx)">
                <span>{{ dictionary.buttons.removeFromPortfolioShort }}</span>
                <i class="ri-folder-reduce-line"/>
              </div>
              <div class="symbolOption toggle" :class="{optionOn: row.emailPush}"
                   @click="updateSymbolOption(row, 'emailPush')">
                <i class="ri-mail-line"/>
              </div>
              <div class="symbolOption toggle" :class="{optionOn: row.soundNotification}"
                   @click="updateSymbolOption(row, 'soundNotification')">
                <i class="ri-volume-up-line"/>
              </div>
              <div class="symbolOption toggle" :class="{optionOn: row.isFavorite}"
                   @click="updateSymbolOption(row, 'isFavorite')">
                <i class="ri-star-fill"/>
              </div>
            </div>
          </td>
          <td v-show="hasEvents()">
            <div class="notificationTags" v-if="row.events?.length > 0">
              <div class="eventRow">
                <div v-for="(event, eIndx) in row.events" class="tag event">
                  <Popper
                      arrow
                      :hover="true"
                      :content="`${event.label}${extraEventData(event)}`"
                  >
                    <i class="ri-calendar-line"/>{{ event.type.substr(0, 1) }}
                  </Popper>
                </div>
              </div>
            </div>
          </td>
          <td>
            <symbol-label :key="`s-${row.symbol}`"
                          :pre-market-quote="row.tradingData?.preMarket"
                          :post-market-quote="row.tradingData?.postMarket"
                          :symbol="row.tradingData" :price-only="true"
                          :show-prePost="true" :always-on="true"/>
          </td>
 <VolumeCell :volume="row.tradingData?.formattedVolume" :trend="row.tradingData?.volumeTrend" />
          <td>
            {{ row.tradingData?.formattedMarketCap }}
          </td>
            <NumericCell
              :value="row.tradingData?.atr?.atrPercent"
              :ranges="[0, 1, 3, 5, 7]"
              :classes="['low','moderate','medium','high','extreme']"
          />
          <TrendIconCell :trend="row.tradingData?.atr?.atrTrend"/>
          <TrendIconCell :trend="row.tradingData?.obvTrend"/>
          <NumericCell v-for="period in ['5D','10D','1M']" :value="row.eods?.[period]"/>
          <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3" :previousClose="row.tradingData?.price" :direction="'top'"
                         :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma50"
                         @deltaChange="deltaChange(row.symbol, $event, 'ma50D')"/>
          <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3" :previousClose="row.tradingData?.price" :direction="'bottom'"
                         :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma200"
                         @deltaChange="deltaChange(row.symbol, $event, 'ma200D')"/>
          <YearlyRangeCell :delta-change="this.deltaChange" :row="row"/>
          <RSICell :rsi="row.tradingData?.rsi"/>
          <MACDCell :macd="row.tradingData?.macd"/>
        

          <NumericCell :noColor="true" :value="row.tradingData?.eps"/>
          <NumericCell :noColor="true" :value="row.tradingData?.pe"/>
          <IndustrySectorCell :symbol="row"/>

        </tr>
      </template>
    </DataGridTable>
  </main>
</template>

<script>

import {useWatchlist} from "../stores/watchlist";
import {getCurrentInstance, ref} from "vue";
import UserService from '../services/user';
import SymbolLabel from "../components/symbols/symbol";
import eventBus from "../services/events";
import SymbolView from "./SymbolView";
import SymbolsSearch from "../components/ui/SymbolsSearch";
import ToggleSwitch from "../components/ui/ToggleSwitch";
import SymbolLogo from "../components/symbols/symbolLogo";
import EmptyPortfolio from "../components/ui/EmptyPortfolio";
import DataGridTable from "../components/dataGrid/DataGridTable";
import HeadCell from "../components/dataGrid/HeadCell";
import Popper from "vue3-popper";
import PrePostQuote from "../components/symbols/PrePostQuote";
import TableToolbar from "../components/ui/TableToolbar";
import SymbolLink from "../components/symbols/symbolLink";
import PriceDistance from "../components/priceTags/priceDistance";
import SymbolTag from "../components/symbols/symbolTag";
import PriceRange from "../components/priceTags/priceRange";
import NumericCell from "../components/dataGrid/NumericCell";
import YearlyRangeCell from "../components/dataGrid/YearlyRangeCell";
import RSICell from "../components/dataGrid/RSICell";
import MACDCell from "../components/dataGrid/MACDCell";
import TrendIconCell from "../components/dataGrid/TrendIconCell";
import IndustrySectorCell from "../components/dataGrid/IndustrySectorCell";
import VolumeCell from "../components/dataGrid/VolumeCell";

export default {
  name: "WatchlistView",
  components: {
    IndustrySectorCell,
    VolumeCell,
    TrendIconCell,
    MACDCell,
    RSICell,
    YearlyRangeCell,
    NumericCell,
    PriceRange,
    SymbolTag,
    PriceDistance,
    SymbolLink,
    TableToolbar,
    PrePostQuote,
    HeadCell,
    DataGridTable,
    EmptyPortfolio,
    SymbolLogo,
    ToggleSwitch,
    SymbolsSearch,
    SymbolView,
    SymbolLabel,
    Popper
  },
  setup() {

    const app = getCurrentInstance();
    const watchlistStore = useWatchlist();
    const wl = ref([]);

    watchlistStore.$subscribe((mutation, state) => {

      app.ctx.loadWatchlist();
      if (app.data.isInitialLoad) {
        app.data.isInitialLoad = false;
      } else {
      }
    });
  },
  data() {
    return {
      notificationsAllowed: false,
      watchlist: [],
      isLoading: true,
      selectedSymbols: [],
      searchQuery: null,
      isInitialLoad: true,
      dontReload: false,
      userSettings: null,
    }
  },
  computed: {
    allowNotifications() {

      return this.notificationsAllowed;
    },
    watchlistSymbols() {

      return !this.searchQuery || (this.searchQuery.toString().trim().length === 0) ? this.watchlist : this.watchlist.filter(symbol => {

        if (symbol.symbol.toLowerCase().includes(this.searchQuery.toLowerCase())) return true;
        if (symbol.name?.toLowerCase().includes(this.searchQuery.toLowerCase())) return true;
      });
    }
  },
  methods: {
    async removeSelected() {

      try {

        this.dontReload = true;

        for (let symbol of this.selectedSymbols) {

          const index = this.watchlist.findIndex(wlSymbol => wlSymbol.symbol === symbol);
          this.watchlist.splice(index, 1);
        }

        useWatchlist().removeSymbol(this.selectedSymbols);
        await UserService.removeSymbolFromWatchlist(this.selectedSymbols.join(','))
        this.dontReload = false;
      } catch (e) {

      }

      this.selectedSymbols = [];
    },
    toggleMultiSelect(symbol) {
      const index = this.selectedSymbols.indexOf(symbol);
      if (index === -1) {
        this.selectedSymbols.push(symbol);
      } else {
        this.selectedSymbols.splice(index, 1);
      }
    },
    deltaChange(symbol, value, key) {

      this.watchlist = this.watchlist.map(row => {
        if (row.symbol === symbol) {
          row[key] = value;
        }
        return row;
      });
    },
    async loadUserSettings() {

      this.userSettings = await UserService.getUserSettings();
      this.notificationsAllowed = this.userSettings.allowNotifications;

    },
    async allowNotificationsChange() {

      this.notificationsAllowed = !this.notificationsAllowed;
      await UserService.updateNotificationsSettings({
        allowNotifications: this.notificationsAllowed
      });
    },
    hasEvents() {

      return this.watchlist.some(symbol => symbol?.events?.length > 0)
    },
    allOn(option) {

      if (!this.isInitialLoad) return false;
      return this.watchlist.every(symbol => symbol[option] === true);
    },
    async updateSymbolOptionForAll(option) {

      const isAllOn = this.allOn(option);

      await UserService.updateSymbolOptionForAll(option, !isAllOn);
      this.watchlist = this.watchlist.map(symbol => {

        symbol[option] = !isAllOn;
        return symbol;
      });
    },
    searchSymbolClick() {

      eventBus.emit('searchSymbolClick')
    },
    async loadWatchlist() {


      if (!this.dontReload) {
        // this.isLoading = true;
        let watchlist = await UserService.getUsersWatchlist();
        watchlist = watchlist.map(symbol => {

          symbol.isLoading = false;
          return symbol;
        });

        this.watchlist = watchlist;
        this.isLoading = false;
      }
    },
    async updateSymbolOption(symbol, option) {

      symbol[option] = !symbol[option];
      await UserService.updateSymbolOption(symbol.symbol, option, symbol[option]);
    },
    extraEventData(event) {
      if (event.ratio) return ` (${event.ratio})`
      if (event.epsEstimate) return ` (EPS est. $${event.epsEstimate})`
      if (event.dividendRate) return ` (Div. $${event.dividendRate})`
    },
    async removeSymbol(symbol) {

      try {

        this.dontReload = true;
        const index = this.watchlist.findIndex(wlSymbol => wlSymbol.symbol === symbol.symbol);
        this.watchlist.splice(index, 1);
        useWatchlist().removeSymbol(symbol.symbol);
        await UserService.removeSymbolFromWatchlist(symbol.symbol)
        this.dontReload = false;
      } catch (e) {

      }
    }
  },
  async mounted() {
    await Promise.all([this.loadUserSettings(), this.loadWatchlist()]);
    // await this.loadUserSettings();
    // await this.loadWatchlist();
  }
}
</script>

<style scoped lang="scss">
@import "../assets/style/general";

.settings {
  padding: 0 !important;
  display: flex;
  align-items: center;
  width: fit-content;
  height: 37px;
  overflow: hidden;
}

.symbolRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--notficationBorderColor);

  &:hover {
    background: var(--tableHoverColor);
  }

  &.header {
    &:hover {
      background: var(--bgColor);
    }
  }

  a {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  .symbolName {
    font-weight: 600;
    font-size: 18px;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .companyName {
    padding: 0 1rem;
    unicode-bidi: plaintext;
  }


  .companyName[dir="rtl"] {
    text-align: right;
  }

  .remove {
    padding: 1rem;
    cursor: pointer;
  }
}

.symbolContainer {
  display: flex;
  align-items: center;
  gap: 0;
}

.notificationTags {
  padding: 5px;
  background: black;
  width: fit-content;
  color: white;
  border-radius: 5px;
  margin: 0;
  margin-top: 0;
}

#emptyPortfolio {
  max-width: 720px;
  margin: 0 auto;
  @media (max-width: 660px) {
    width: 90%;
  }
}

#searchSymbol {
  cursor: pointer;
  background: var(--highlightColorOpacity);
  padding: $smallGap;
  border-radius: 8px;
  color: var(--highlightColor);
  text-align: center;
  width: fit-content;
  font-weight: 500;
  margin: 5px auto;
}

.expl {
  background: var(--highlightColorOpacity);
  padding: $smallGap;
  margin: 5px;
  border-radius: 8px;
  color: var(--highlightColor);
  font-weight: 500;
}

.watchlistSize {
  padding: $smallGap;
  font-weight: 600;
  text-align: center;
  padding: 0;

  &.listFull {
    color: var(--red);
  }
}


.symbolOption {
  padding: 1rem;
  cursor: pointer;
  text-align: center;

  i {
    display: block;
  }

  label {
    font-size: 12px;
  }

  &.toggle {
    color: var(--gray);
    background: var(--bgColor);
    transition: $toggleTransition;
    opacity: .5;
  }

  &.optionOn {
    opacity: 1;
    color: var(--lightYellow);
    background: var(--lightYellowOpacity);
  }
}

.settings {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

#searchWrapper {
  position: relative;
  z-index: 3;

  i {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  input {
    padding: 5px;
    border-radius: 50px;
    border: 2px solid var(--notficationBorderColor);
    width: 160px;
    transition: all .1s ease-in;

    &.autoWidth {
      width: auto;
    }

    @media(max-width: 660px) {
      width: 210px;
      margin-right: auto;
    }

    &:focus, &:active {
      border: 2px solid var(--highlightColor);
      background: var(--bgColor);
    }
  }
}


#searchWrapper[dir="rtl"] {
  i {
    right: 10px;
  }

  input {
    padding-right: 30px;
  }
}


#searchWrapper[dir="ltr"] {
  i {
    left: 10px !important;
  }

  input {
    padding-left: 30px !important;

    &:focus,
    &:active {
      outline: none;
    }
  }
}

.searchBar {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  position: relative;
  margin-bottom: 5px;

  #firstWrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    @media (max-width: 660px) {
      flex-direction: column;
      gap: 5px;
      padding: 10px 0;
    }
  }
}

.addRemoveSymbol {
  flex: 0 0 48px !important;
  width: 48px;
  z-index: 0;
  justify-content: center;
  align-items: center;
  height: 48px;
  border-radius: 0;
  font-size: 32px;
  padding: 0;

  span {
    margin-top: -6px;
  }

  &[dir="rtl"] {
    margin-left: unset;
    margin-right: auto;
  }

  i {
    display: none;
  }

  @media(max-width: 660px) {

    box-sizing: border-box;
    justify-content: center;
    align-items: center;
    i {
      display: block;
    }

    span {
      //display: none;
    }
  }
}

.symbolCell {
  display: flex;
  align-items: center;
  gap: 15px;

  @media (max-width: 768px) {
    gap: 15px;
  }

  label {
    padding: 10px;
    cursor: pointer;
    margin: -10px;

    input {
      width: 18px;
      height: 18px;
    }
  }
}

.removeMultipleButton {
  cursor: pointer;

  bottom: 20px;
  background: var(--red-dark);
  color: var(--textColor);
  font-weight: 600;
  border-radius: 5px;
  text-align: center;
  border: 2px solid var(--red-dark);
  z-index: 99;
  box-shadow: 0 5px 10px 0 var(--opacityRed);

  flex: 0 1 fit-content;
  margin-top: 0;
  padding: 0 15px;
  height: 26px;
  line-height: 25px;

  &[dir="rtl"] {
    left: unset;
    right: 75%;
    transform: translateX(50%);
    padding: 6px;
    margin-top: unset;
    height: unset;
    flex: unset;
    line-height: unset;
  }

  @media (max-width: 768px) {
    left: 75%;
    position: fixed;
    transform: translateX(-50%);
    width: 160px;

  }
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.slide-fade-enter-active {
  transition: all 300ms cubic-bezier(0.4, 0, 0.1, .6);
}

.slide-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.slide-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.slide-fade-leave-active {
  transition: all 200ms ease-out;
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

</style>
